/**
 * GuidedSurveyWizard
 *
 * P_GUIDED_SURVEY_EXPERIENCES_1.0
 *
 * Outcome-first wizard that asks the author "What do you want to
 * learn?" and generates a draft survey inside the existing Survey
 * Builder. The user always feels they are inside one Survey system —
 * we never claim to be a separate engine.
 *
 * Vocabulary discipline (locked in PRD):
 *   - Speak in business outcomes. Avoid engine vocabulary
 *     ("node", "next_step", "graph", "branching topology") in any
 *     user-facing copy.
 *
 * Single-engine contract:
 *   - On submit, calls POST /api/surveys/guided/instantiate which
 *     creates a regular Survey document via the same Pydantic models
 *     used by the Builder. We then navigate the author to the
 *     existing /surveys/:id editor — they edit / preview / publish
 *     using the same screens they already know.
 *
 * Steps
 *   1) Choose outcome — a grid of curated outcome cards.
 *   2) Tell us a few details — location + optional recipe-specific
 *      parameters.
 *   3) Review & create — show the recipe's preview questions and
 *      commit on confirm.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Sparkles, X, Compass } from 'lucide-react';

import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

import { surveysAPI } from '../api';
import OrgLocationPicker from '../components/OrgLocationPicker';

const STEPS = ['outcome', 'details', 'review'];

const GuidedSurveyWizard = ({ open, onOpenChange, onCreated, presetOutcomeId = null }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const lang = i18n.language || 'en';
    const tr = (bag) => (bag && (bag[lang] || bag.en)) || '';

    // ── state ───────────────────────────────────────────────────
    const [step, setStep] = useState(0);
    const [outcomes, setOutcomes] = useState([]);
    const [selectedOutcomeId, setSelectedOutcomeId] = useState(null);
    const [outcomeDetail, setOutcomeDetail] = useState(null);  // populated for steps 2 + 3
    const [params, setParams] = useState({});  // {param_name: value}
    const [organizationId, setOrganizationId] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [pickerMeta, setPickerMeta] = useState({ loaded: false, orgCount: 0, locationCount: 0 });
    const [saving, setSaving] = useState(false);

    const selectedOutcome = useMemo(
        () => outcomes.find((o) => o.outcome_id === selectedOutcomeId) || null,
        [outcomes, selectedOutcomeId],
    );

    // ── reset on close ─────────────────────────────────────────
    const reset = () => {
        setStep(0);
        setSelectedOutcomeId(null);
        setOutcomeDetail(null);
        setParams({});
        setOrganizationId('');
        setSelectedLocation('');
        setSaving(false);
    };

    // ── load outcomes on open (org/location handled by OrgLocationPicker) ──
    useEffect(() => {
        if (!open) return;
        surveysAPI.guided.listOutcomes()
            .then((d) => setOutcomes(d.outcomes || []))
            .catch((err) => {
                toast.error(err?.response?.data?.detail || t('surveys.guided.loadFailed', 'Could not load outcomes.'));
            });
    }, [open, t]);

    // P_SURVEY_INTELLIGENCE_ENGINE_1.0 — Slice A. If a campaign
    // preselected an outcome, jump straight to step 2 (Details) and
    // load that outcome's detail. The user never sees the outcome
    // chooser — they already chose the business initiative.
    useEffect(() => {
        if (!open || !presetOutcomeId) return;
        let cancelled = false;
        setSelectedOutcomeId(presetOutcomeId);
        surveysAPI.guided.getOutcome(presetOutcomeId)
            .then((d) => {
                if (cancelled) return;
                setOutcomeDetail(d);
                const seed = {};
                (d.parameters || []).forEach((p) => { seed[p.name] = ''; });
                setParams(seed);
                setStep(1);
            })
            .catch((err) => {
                if (cancelled) return;
                toast.error(err?.response?.data?.detail || t('surveys.guided.loadFailed', 'Could not load outcome.'));
            });
        return () => { cancelled = true; };
    }, [open, presetOutcomeId, t]);

    // ── load full outcome detail when moving from step 1 → 2 ──
    const loadDetailAndAdvance = async () => {
        if (!selectedOutcomeId) return;
        try {
            const d = await surveysAPI.guided.getOutcome(selectedOutcomeId);
            setOutcomeDetail(d);
            // initialize params with empty strings for the recipe's required fields
            const seed = {};
            (d.parameters || []).forEach((p) => { seed[p.name] = params[p.name] || ''; });
            setParams(seed);
            setStep(1);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('surveys.guided.loadFailed', 'Could not load outcome.'));
        }
    };

    const needsLocationPrompt = pickerMeta.locationCount > 1 && !selectedLocation;

    const missingRequiredParam = (outcomeDetail?.parameters || []).find(
        (p) => p.required && !(params[p.name] || '').trim(),
    );

    const handleCreate = async () => {
        if (!selectedOutcome || !outcomeDetail) return;
        if (!organizationId) {
            toast.error(t('surveys.selectOrganizationFirst', 'Please select an organization first.'));
            return;
        }
        if (needsLocationPrompt) {
            toast.error(t('surveys.selectLocationFirst', 'Please select a location before publishing.'));
            return;
        }
        if (missingRequiredParam) {
            toast.error(t('surveys.guided.fillRequired', 'Please fill in the required details.'));
            return;
        }
        // P_GUIDED_SURVEY_EXPERIENCES_1.0 — progress UX (honest)
        // Close the wizard immediately and hand off to the dedicated
        // progress screen. The screen owns the API call, the polling,
        // and the completion CTA. We do NOT refresh the parent
        // SurveysList here — that list is unmounted while the user is
        // on /surveys/guided/creating and will reload naturally when
        // they return via "Back to Surveys" or browser navigation.
        const payload = {
            outcome_id: selectedOutcomeId,
            parameters: params,
            authoring_language: lang,
            organization_id: organizationId,
        };
        if (selectedLocation) payload.location_id = selectedLocation;
        onOpenChange?.(false);
        reset();
        navigate('/surveys/guided/creating', {
            state: {
                payload,
                outcomeMeta: {
                    icon: selectedOutcome.icon,
                    title: selectedOutcome.title,
                },
            },
        });
    };

    // ── render helpers ─────────────────────────────────────────
    const renderOutcomeStep = () => (
        <div className="space-y-4" data-testid="guided-step-outcome">
            <p className="text-sm text-slate-600 dark:text-slate-300">
                {t('surveys.guided.outcomePrompt', 'Pick what you want to learn — the platform builds the survey for you.')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[58vh] overflow-y-auto pr-1">
                {outcomes.map((o) => {
                    const selected = o.outcome_id === selectedOutcomeId;
                    return (
                        <button
                            key={o.outcome_id}
                            type="button"
                            onClick={() => setSelectedOutcomeId(o.outcome_id)}
                            data-testid={`guided-outcome-${o.outcome_id}`}
                            data-insight-id="surveys.guided.outcome-card"
                            data-insight-label="Outcome card"
                            className={[
                                'text-left rounded-lg border p-4 transition-all',
                                'hover:shadow-md hover:-translate-y-0.5',
                                selected
                                    ? 'border-fuchsia-500 ring-2 ring-fuchsia-300 bg-fuchsia-50/60 dark:bg-fuchsia-950/30'
                                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40',
                            ].join(' ')}
                        >
                            <div className="flex items-start gap-3">
                                <div className="text-2xl leading-none" aria-hidden="true">{o.icon}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm text-slate-900 dark:text-slate-100">{tr(o.title)}</div>
                                    <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">{tr(o.learn_prompt)}</div>
                                </div>
                                {selected && (
                                    <div className="text-fuchsia-600 dark:text-fuchsia-300 mt-1">
                                        <Check className="w-4 h-4" />
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );

    const renderDetailsStep = () => (
        <div className="space-y-4" data-testid="guided-step-details">
            <p className="text-sm text-slate-600 dark:text-slate-300">
                {t('surveys.guided.detailsPrompt', 'A few quick details so we can tailor the survey to your business.')}
            </p>

            {/* Portal-owned context — Organization → Location. The URL is auto-generated. */}
            <OrgLocationPicker
                organizationId={organizationId}
                locationId={selectedLocation}
                onChange={({ organizationId: o, locationId: l }) => {
                    setOrganizationId(o);
                    setSelectedLocation(l || '');
                }}
                onMeta={setPickerMeta}
                testIdPrefix="guided"
            />

            {/* Recipe-specific parameters */}
            {(outcomeDetail?.parameters || []).map((p) => (
                <div key={p.name} className="space-y-1.5">
                    <Label htmlFor={`guided-param-${p.name}`}>
                        {tr(p.label)}
                        {p.required && <span className="text-red-500 ml-0.5">*</span>}
                    </Label>
                    <Input
                        id={`guided-param-${p.name}`}
                        value={params[p.name] || ''}
                        onChange={(e) => setParams((s) => ({ ...s, [p.name]: e.target.value }))}
                        placeholder={tr(p.placeholder)}
                        data-testid={`guided-param-${p.name}`}
                    />
                </div>
            ))}

            {(outcomeDetail?.parameters || []).length === 0 && (
                <p className="text-xs italic text-slate-500 dark:text-slate-400">
                    {t('surveys.guided.noParams', "No extra details needed — we'll generate it right away.")}
                </p>
            )}
        </div>
    );

    const renderReviewStep = () => {
        const previewQs = outcomeDetail?.preview_questions || [];
        return (
            <div className="space-y-4" data-testid="guided-step-review">
                <div className="rounded-md bg-fuchsia-50/60 dark:bg-fuchsia-950/30 border border-fuchsia-200 dark:border-fuchsia-800 px-3 py-2 text-sm">
                    <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-fuchsia-600 dark:text-fuchsia-300 mt-0.5" />
                        <div>
                            <div className="font-semibold text-fuchsia-900 dark:text-fuchsia-100">
                                {selectedOutcome?.icon} {tr(selectedOutcome?.title)}
                            </div>
                            <div className="text-xs text-fuchsia-800 dark:text-fuchsia-200 mt-0.5">
                                {tr(selectedOutcome?.learn_prompt)}
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500 mb-2 block">
                        {t('surveys.guided.questionsPreview', "Here's what your survey will ask")}
                    </Label>
                    <ol className="space-y-2 max-h-[42vh] overflow-y-auto pr-1" data-testid="guided-review-questions">
                        {previewQs.map((q, idx) => {
                            // Inline customer params into the preview so the
                            // author sees their location/event in context.
                            const renderText = (s) => {
                                let out = s || '';
                                Object.entries(params || {}).forEach(([k, v]) => {
                                    const val = (v || '').trim();
                                    if (val) {
                                        out = out
                                            .replace(/\{location\}/g, val)
                                            .replace(/\{event\}/g, val)
                                            .replace(new RegExp(`\\{${k}\\}`, 'g'), val);
                                    }
                                });
                                return out;
                            };
                            return (
                                <li
                                    key={q.id}
                                    className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 px-3 py-2 text-sm"
                                    data-testid={`guided-review-q-${idx}`}
                                >
                                    <div className="text-slate-800 dark:text-slate-100">
                                        <span className="font-semibold text-slate-500 mr-1">{idx + 1}.</span>
                                        {renderText(tr(q.text))}
                                    </div>
                                    {q.choices && q.choices.length > 0 && (
                                        <ul className="mt-1 pl-5 text-xs text-slate-600 dark:text-slate-300 list-disc">
                                            {q.choices.map((c) => (
                                                <li key={c.id}>{tr(c.label)}</li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                    <p className="text-xs text-slate-500 mt-2">
                        {t('surveys.guided.editLater', "You'll land in the Survey Builder right after — change anything you like before publishing.")}
                    </p>
                </div>
            </div>
        );
    };

    const isStep0 = step === 0;
    const isStep1 = step === 1;
    const isStep2 = step === 2;
    const canAdvanceFromOutcome = !!selectedOutcomeId;
    const canAdvanceFromDetails = !needsLocationPrompt && !missingRequiredParam;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange?.(v); }}>
            <DialogContent
                className="max-w-2xl"
                data-testid="guided-survey-wizard"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <div className="flex items-center gap-2 text-fuchsia-600 dark:text-fuchsia-300 text-xs font-semibold uppercase tracking-wide">
                        <Compass className="w-3.5 h-3.5" />
                        <span>
                            {t('surveys.guided.eyebrow', 'Guided Survey · Step {{current}} of {{total}}', {
                                current: step + 1,
                                total: STEPS.length,
                            })}
                        </span>
                    </div>
                    <DialogTitle className="text-lg" data-testid="guided-survey-wizard-title">
                        {isStep0 && t('surveys.guided.titleOutcome', 'What do you want to learn?')}
                        {isStep1 && t('surveys.guided.titleDetails', 'Tell us a few details')}
                        {isStep2 && t('surveys.guided.titleReview', 'Review and create')}
                    </DialogTitle>
                </DialogHeader>

                {isStep0 && renderOutcomeStep()}
                {isStep1 && renderDetailsStep()}
                {isStep2 && renderReviewStep()}

                {/* Footer controls */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-200 dark:border-slate-700 mt-2">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => { onOpenChange?.(false); reset(); }}
                        data-testid="guided-cancel"
                    >
                        <X className="w-3.5 h-3.5 mr-1" />
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <div className="flex items-center gap-2">
                        {!isStep0 && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    // When launched from a campaign, the
                                    // outcome chooser is not part of the
                                    // user's mental model — Back from
                                    // step 1 (Details) closes instead.
                                    if (presetOutcomeId && step === 1) {
                                        onOpenChange?.(false);
                                        reset();
                                        return;
                                    }
                                    setStep((s) => Math.max(0, s - 1));
                                }}
                                disabled={saving}
                                data-testid="guided-back"
                            >
                                <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                                {t('common.back', 'Back')}
                            </Button>
                        )}
                        {isStep0 && (
                            <Button
                                type="button"
                                size="sm"
                                onClick={loadDetailAndAdvance}
                                disabled={!canAdvanceFromOutcome}
                                data-testid="guided-next-from-outcome"
                            >
                                {t('common.next', 'Next')}
                                <ArrowRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                        )}
                        {isStep1 && (
                            <Button
                                type="button"
                                size="sm"
                                onClick={() => setStep(2)}
                                disabled={!canAdvanceFromDetails}
                                data-testid="guided-next-from-details"
                            >
                                {t('common.next', 'Next')}
                                <ArrowRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                        )}
                        {isStep2 && (
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleCreate}
                                disabled={saving}
                                data-testid="guided-create-btn"
                            >
                                {saving ? (
                                    t('common.creating', 'Creating…')
                                ) : (
                                    <>
                                        <Sparkles className="w-3.5 h-3.5 mr-1" />
                                        {t('surveys.guided.create', 'Create my guided survey')}
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default GuidedSurveyWizard;
